import type { FormData } from "@amzn/aquila-service-type-script-client";
import type { AsyncStatus, UseAsyncActions } from "@react-hookz/web";
import { useAsync } from "@react-hookz/web";
import { useEffect } from "react";
import { useStore } from "zustand";

import { getAquilaApiClient } from "../../../../api/aquilaApi/aquilaApi";
import { CreateFeedbackNotificationMessage } from "../../../../components/common/notification.components";
import { authStore } from "../../../../store/auth";
import {
    notificationStore,
    NotificationType,
} from "../../../../store/notification";
import type { AquilaReportIssueTriggerType } from "../../../../types/aquila";
import { getAdvertiserId } from "../../../../utils/permission.utils";
import { useReportIssueContextDataSync } from "../useReportIssueContextDataSync/useReportIssueContextDataSync";
import { useReportIssueCti } from "../useReportIssueCti/useReportIssueCti";

interface SubmitReportIssueFormProps {
    reportIssueFormConfig: { configId: string; awsAccountId: string };
    trigger: AquilaReportIssueTriggerType;
}

export const useSubmitReportIssueForm = ({
    reportIssueFormConfig,
    trigger,
}: SubmitReportIssueFormProps): [
    AsyncStatus,
    UseAsyncActions<string, [formData: FormData[]]>,
] => {
    const [upsertNotification] = useStore(notificationStore, (state) => [
        state.upsertNotification,
    ]);
    const sfId = getAdvertiserId() ?? "";
    const [username] = useStore(authStore, (state) => [state.username]);
    const [getCti] = useReportIssueCti(trigger);
    const { getReportIssueContextData } = useReportIssueContextDataSync();

    const [submitFormConfigAsyncState, submitFormConfigAsyncAction] = useAsync(
        async (formData: FormData[]) => {
            if (!reportIssueFormConfig.configId) {
                throw new Error("Report issue form config is not provided");
            }
            const cti = await getCti(formData);
            const ctiTag = `${cti.category} - ${cti.type} - ${cti.item}`;
            const issueType = formData.find(
                (data) => data.field === "issueType",
            );
            const issueTypeValue = issueType?.value as string;
            const triggerAction = trigger
                ? ` - [${trigger}${issueTypeValue ? `-${issueTypeValue}` : ""}]`
                : "";
            const response = await (
                await getAquilaApiClient()
            ).createFeedback({
                configId: reportIssueFormConfig.configId,
                awsAccountId: reportIssueFormConfig.awsAccountId,
                requesterAlias: username,
                formData: formData.filter((data) => data.value !== ""),
                additionalFormContext: getReportIssueContextData(trigger),
                ticketData: {
                    cti,
                    tags: [ctiTag],
                    title: `IHub Issue - ${username}${triggerAction}${sfId ? ` - ${sfId}` : ""}`,
                },
            });
            upsertNotification(
                <CreateFeedbackNotificationMessage
                    ticketId={response.ticketId}
                />,
                NotificationType.SUCCESS,
            );
            return response?.ticketId ?? "";
        },
    );

    useEffect(() => {
        if (submitFormConfigAsyncState.status === "error") {
            console.error(submitFormConfigAsyncState.error);
            upsertNotification(
                "Failed to load report issue form details. Please try again later.",
                NotificationType.ERROR,
                "getReportIssueFormError",
            );
        }
    }, [submitFormConfigAsyncState.status]);

    return [submitFormConfigAsyncState.status, submitFormConfigAsyncAction];
};
